import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Checkbox,
	Confirm,
	Input,
	Modal,
	SpinnerOverlay,
	SubmissionButtons } from '@components/common';
import UploadLogo from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo';
import { TCreateUploadUrl } from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo/UploadLogo';

import { useMutateCheckAssetTypeName } from '@hooks/assets/assetTypes/useMutateCheckAssetTypeName';
import { useMutateCreateAssetType } from '@hooks/assets/assetTypes/useMutateCreateAssetType';
import { useMutateUpdateAssetType } from '@hooks/assets/assetTypes/useMutateUpdateAssetType';
import { useMutateUploadAssetIcon } from '@hooks/assets/assetTypes/useMutateUploadAssetIcon';
import { useQueryNonRequiredFieldsForAssetType } from '@hooks/assets/assetTypes/useQueryAssetTypeNonRequiredFields';

import { ICheckboxesEntity } from '@interfaces/platform/manageAssetTypes';

import { EValidationMode } from '@enums/validationMode';

import { modalCloseConfirm } from '@constants/modals';
import { regExpNumbersAndLetters } from '@constants/regExp';
import {
	acceptedFileTypes,
	ASSET_ICON_MAX_SIZE,
	assetTypeInputLabel,
	assetTypeInputName,
	assetTypeInputPlaceholder,
	fileErrors,
	modalWidth,
	supportsText,
} from '../constants';

import AssetLogo from './AssetLogo';
import { assetTypeSchema } from './schema';

import styles from './AssetTypeForm.module.scss';

interface IFormValues {
	assetTypeName: string;
}

interface IAssetTypeFormProps {
	closeFormModal: () => void;
	selectedFields?: string[];
	assetTypeName?: string;
	id?: string;
	assetIconId?: string | null;
	setIsDirtyFormModal: (isDirty: boolean) => void;
	onClose: () => void;
	isWithSubType?: boolean;
}

const AssetTypeForm = ({
	assetIconId = null,
	assetTypeName,
	closeFormModal,
	id,
	onClose,
	selectedFields,
	setIsDirtyFormModal,
	isWithSubType = false,
}: IAssetTypeFormProps) => {
	const [isUpdateConfirm, setIsUpdateConfirm] = useState(false);
	const [isUploadImageConfirm, setIsUploadImageConfirm] = useState(false);
	const [isDirtyUploadModal, setIsDirtyUploadModal] = useState(false);

	const { handleCheckAssetTypeName } = useMutateCheckAssetTypeName();

	const {
		formState: { errors, isDirty },
		register,
		setValue,
		handleSubmit,
		watch,
	} = useForm<IFormValues>({
		mode: EValidationMode.onSubmit,
		resolver: yupResolver(assetTypeSchema(handleCheckAssetTypeName, assetTypeName)),
		defaultValues: {
			assetTypeName: assetTypeName || '',
		},
	});

	const inputValue = watch(assetTypeInputName);

	const [isUploadImageModal, setIsUploadImageModal] = useState(false);
	const [iconId, setIconId] = useState<string | null>(assetIconId);
	const [fieldsList, setFieldsList] = useState<ICheckboxesEntity[]>([]);

	const openUploadImageModal = useCallback(() => {
		setIsUploadImageModal(true);
		setIsDirtyUploadModal(false);
	}, []);

	const closeIconModal = useCallback(() => {
		setIsUploadImageModal(false);
		setIsUploadImageConfirm(false);
	}, []);

	const toggleUploadImageConfirm = useCallback(() => setIsUploadImageConfirm(prevState => !prevState), []);

	const closeUploadImageConfirmHandler = useCallback(() => {
		if (isDirtyUploadModal) {
			setIsUploadImageConfirm(prevState => !prevState);
		} else {
			closeIconModal();
		}
	}, [closeIconModal, isDirtyUploadModal]);

	const openUpdateConfirm = useCallback(() => setIsUpdateConfirm(true), []);
	const closeUpdateConfirm = useCallback(() => setIsUpdateConfirm(false), []);

	const { nonRequiredFields, isLoadingNonRequiredFields } = useQueryNonRequiredFieldsForAssetType();

	const onImageUpload = useCallback((id?: string) => {
		if (id) {
			setIconId(id);
			setIsDirtyFormModal(true);
			setIsUploadImageModal(false);
		}
	}, [setIsDirtyFormModal]);

	const {
		handleCreateAssetType,
		isLoadingCreateAssetType,
	} = useMutateCreateAssetType({
		onSuccess: closeFormModal,
	});

	const {
		handleUpdateAssetType,
		isLoadingUpdateAssetType,
	} = useMutateUpdateAssetType();

	const {
		createUploadIconUrl,
		isLoadingCreateUploadIconUrl,
		progress,
	} = useMutateUploadAssetIcon(onImageUpload);

	const isLoading = isLoadingCreateAssetType || isLoadingUpdateAssetType || isLoadingNonRequiredFields;

	const inputError = errors?.assetTypeName?.message;

	useEffect(() => {
		setFieldsList(
			nonRequiredFields?.map(item => ({
				value: item?.name,
				label: item?.title,
				isChecked: selectedFields ? selectedFields.includes(item?.name) : false,
			})) || [],
		);
	}, [selectedFields, nonRequiredFields]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const onChangeHandler = useCallback(({ value, isChecked }: ICheckboxesEntity) => {
		setIsDirtyFormModal(true);
		setFieldsList((prevState) => prevState.map((option) =>
			option.value === value
				? ({
					...option,
					isChecked: !isChecked,
				})
				: option,
		));
	}, [setIsDirtyFormModal]);

	const updateAssetTypeHandler = useCallback(() => {
		if (!id) return;

		handleUpdateAssetType({
			requiredSystemFieldNames: fieldsList
				.filter((item) => item.isChecked)
				.map((item) => item.value),
			assetTypeName: inputValue,
			iconId,
			id,
		});

		closeFormModal();
		closeUpdateConfirm();
	}, [
		fieldsList,
		inputValue,
		iconId,
		id,
		closeFormModal,
		closeUpdateConfirm,
		handleUpdateAssetType,
	]);

	const onSubmit = useCallback(() => {
		const fields = fieldsList
			.filter((item) => item.isChecked)
			.map((item) => item.value);

		if (id) {
			openUpdateConfirm();
		} else {
			handleCreateAssetType({
				requiredSystemFieldNames: fields,
				assetTypeName: inputValue,
				iconId,
			});
		}
	}, [
		fieldsList,
		inputValue,
		iconId,
		id,
		handleCreateAssetType,
		openUpdateConfirm,
	]);

	return (
		<div className={styles.formContainer}>
			{isLoading && <SpinnerOverlay />}
			<Modal
				isOpen={isUpdateConfirm}
				onClose={closeUpdateConfirm}
			>
				<Confirm
					isLoading={isLoadingUpdateAssetType}
					onClose={closeUpdateConfirm}
					onSubmit={updateAssetTypeHandler}
					question='Are you sure you want to update the asset type?'
				>
					Changes will affect organisations using this asset type.
				</Confirm>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose
				isOpen={isUploadImageModal}
				onClose={closeUploadImageConfirmHandler}
				title='Upload image'
				width={modalWidth}
			>
				<UploadLogo
					acceptedLogoFileTypes={acceptedFileTypes}
					createUploadUrl={createUploadIconUrl as unknown as TCreateUploadUrl}
					cropHeight={ASSET_ICON_MAX_SIZE}
					cropWidth={ASSET_ICON_MAX_SIZE}
					isLoadingCreateUploadUrl={isLoadingCreateUploadIconUrl}
					isWithDisabledSubmit={false}
					logoFileErrors={fileErrors}
					maxHeight={ASSET_ICON_MAX_SIZE}
					maxSize={Infinity}
					maxWidth={ASSET_ICON_MAX_SIZE}
					progress={progress}
					setIsDirtyUploadModal={setIsDirtyUploadModal}
					supportsLogoText={supportsText}
					toggleLogoModal={closeUploadImageConfirmHandler}
					unit='px'
				/>
			</Modal>

			<Modal
				isOpen={isUploadImageConfirm}
				onClose={toggleUploadImageConfirm}
			>
				<Confirm
					isLoading={isLoadingUpdateAssetType}
					onClose={toggleUploadImageConfirm}
					onSubmit={closeIconModal}
					{...modalCloseConfirm}
				/>
			</Modal>

			<div className={styles.headerWrapper}>
				<Input
					errorMessage={inputError}
					label={assetTypeInputLabel}
					name={assetTypeInputName}
					placeholder={assetTypeInputPlaceholder}
					regexValidation={regExpNumbersAndLetters}
					register={register}
					required
					setValue={setValue}
				/>
				<AssetLogo
					id={iconId}
					openLogoModal={openUploadImageModal}
				/>
			</div>
			<span className={styles.heading}>Set fields which will be mandatory for filling:</span>
			<div className={styles.checkboxWrapper}>{
				fieldsList.map((item, index) => {
					const onChangeItem = () => onChangeHandler(item);

					const disabled = item.value === 'subType' && !isWithSubType;

					return (
						<Checkbox
							key={index}
							checked={item.isChecked}
							disabled={disabled}
							label={item.label}
							name={item.value}
							onChange={onChangeItem}
						/>
					);
				})
			}
			</div>
			<SubmissionButtons
				disabledPositive={!inputValue}
				negativeHandler={onClose}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
		</div>
	);
};

export default AssetTypeForm;
